import React, { forwardRef, ReactNode, useContext, useMemo } from 'react';
import { DOMProps } from '@react-types/shared';
import { getPlatformStyles, isMobile } from '@direct-frontend/platform-code';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { chain } from 'react-aria';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import { ListSize } from '../ListBoxBase/types';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { MenuListContext } from '../MenuTrigger/utils';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';

import { ListContext } from './utils';

getPlatformStyles({
    common: import('./List.css'),
    desktop: import('./List@desktop.css'),
});

export const cls = cn('List');

export interface ListProps extends TestProps, DOMProps, Loggable {
    /**
     * Размер списка, на мобилках — всегда m
     */
    size: ListSize;

    /**
     * Управление шириной списка
     * @default auto
     */
    width?: 'auto' | 'max' | number;

    /**
     * Элементы списка
     */
    children?: ReactNode;

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Действие при нажатии на один из вложенных интерактивных элементов
     * Не срабатывает на нажатие задизейбленных элементов или элементов без ссылки и обработчика клика
     * @example onItemClick={closePopup}
     */
    onItemClick?: () => void;
}

/**
 * Компонент для отображения списка элементов
 */
export const List = forwardRef<HTMLUListElement, ListProps>(function List(props, ref) {
    const {
        size: propsSize,
        width = 'auto',
        children,
        className,
        'data-testid': dataTestId = 'List',
        onItemClick,
    } = props;
    const size = isMobile() ? 'm' : propsSize;
    const scrollClass = getScrollbarClassName();
    const sizeSkinClass = getSizeSkin(size);
    const { LogProvider } = useLogger('List', props.logNode);
    const { onItemClick: contextOnItemClick } = useContext(MenuListContext);

    const contextValue = useMemo(
        () => ({ size, onItemClick: chain(contextOnItemClick, onItemClick) }),
        [size, contextOnItemClick, onItemClick],
    );
    const isStrictWidth = typeof width === 'number';
    const cssMods = {
        size,
        width: isStrictWidth ? undefined : width,
    };
    const style = isStrictWidth ? { width: `${width}px` } : undefined;
    const colorSkinClass = getColorSkin('gray-neutral', {});

    return (
        <LogProvider>
            <ul
                className={cls(cssMods, [scrollClass, sizeSkinClass, className, colorSkinClass])}
                style={style}
                data-testid={dataTestId}
                ref={ref}
            >
                <ListContext.Provider value={contextValue}>{children}</ListContext.Provider>
            </ul>
        </LogProvider>
    );
});
